"use client";

import React from "react";
import { ScanLine, MousePointerClick, LayoutGrid, Info, ArrowUpRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const metrics = [{
  id: "lcp",
  name: "Largest Contentful Paint (LCP)",
  shortName: "LCP",
  value: 1.2,
  displayValue: "1.2s",
  unit: "s",
  max: 4,
  status: "Optimal",
  description: "Measures loading performance. To provide a good user experience, LCP should occur within 2.5 seconds of when the page first starts loading.",
  seoImpact: "LCP is a Core Web Vital that directly affects search rankings. A poor LCP score signals to search engines that your page is slow to render useful content, potentially lowering your position in SERPs.",
  icon: ScanLine
}, {
  id: "fid",
  name: "First Input Delay (FID)",
  shortName: "FID",
  value: 18,
  displayValue: "18ms",
  unit: "ms",
  max: 200,
  status: "Optimal",
  description: "Measures interactivity. To provide a good user experience, pages should have a FID of 100 milliseconds or less.",
  seoImpact: "FID measures the responsiveness of your site. High delays frustrate users and increase bounce rates, which are negative signals for SEO performance.",
  icon: MousePointerClick
}, {
  id: "cls",
  name: "Cumulative Layout Shift (CLS)",
  shortName: "CLS",
  value: 0.02,
  displayValue: "0.02",
  unit: "",
  max: 0.5,
  status: "Optimal",
  description: "Measures visual stability. To provide a good user experience, pages should maintain a CLS of 0.1. or less.",
  seoImpact: "CLS ensures visual stability. Unexpected layout shifts annoy users and can cause accidental clicks. Search engines penalize sites with poor visual stability.",
  icon: LayoutGrid
}];
export default function MetricIntelligenceHub() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="metric-intelligence-hub" className="dark bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="h-[800px] w-[800px] rounded-full bg-[radial-gradient(circle,var(--primary)_0%,transparent_70%)] opacity-5 blur-[120px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-8">
          {}
          <div className="flex flex-col justify-center lg:col-span-5">
            <div ref={ref} className={`transition-all duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
              <h2 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                Metric <br />
                <span className="text-primary">Intelligence</span> Hub
              </h2>
              <p className="text-muted-foreground mt-6 text-lg leading-relaxed">
                Deep dive into the Core Web Vitals that power the modern web. 
                Understand the technical benchmarks that drive SEO rankings and user retention.
              </p>
              
              <div className="mt-10 flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span className="bg-primary/20 text-primary flex h-2 w-2 rounded-full" />
                  Live Monitoring
                </div>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span className="bg-primary/20 text-primary flex h-2 w-2 rounded-full" />
                  Real-time Analysis
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="flex flex-col gap-6 lg:col-span-7">
            {metrics.map((metric, i) => {
            let percentage = 0;
            if (metric.id === 'lcp') percentage = metric.value / 4 * 100;
            if (metric.id === 'fid') percentage = metric.value / 200 * 100;
            if (metric.id === 'cls') percentage = metric.value / 0.5 * 100;
            return <div key={metric.id} data-index={i} className={`transition-all duration-700 delay-${i * 150} ${isVisible ? "translate-x-0 opacity-100" : "translate-x-10 opacity-0"}`}>
                  <Popover>
                    <PopoverTrigger asChild>
                      <div className="group cursor-pointer outline-none">
                        <Card className="bg-card/50 hover:bg-card hover:border-primary/50 border-border/50 relative overflow-hidden border transition-all duration-300 hover:shadow-lg">
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                          
                          <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-lg border border-white/5">
                                  <metric.icon className="h-5 w-5" />
                                </div>
                                <div>
                                  <CardTitle className="text-xl">{metric.name}</CardTitle>
                                  <CardDescription className="mt-1 font-mono text-xs uppercase tracking-wider">
                                    Core Web Vital
                                  </CardDescription>
                                </div>
                              </div>
                              <Button variant="ghost" size="icon" className="text-muted-foreground group-hover:text-primary">
                                <Info className="h-5 w-5" />
                              </Button>
                            </div>
                          </CardHeader>

                          <CardContent className="pt-4">
                            <div className="mb-4 flex items-end justify-between">
                              <div className="flex flex-col">
                                <span className="text-muted-foreground text-sm font-medium">Current Score</span>
                                <span className="text-3xl font-bold tracking-tight text-white">
                                  {metric.displayValue}
                                </span>
                              </div>
                              <div className="mb-1 flex items-center gap-2">
                                <span className="bg-primary/20 text-primary rounded-full px-2.5 py-0.5 text-xs font-semibold">
                                  {metric.status}
                                </span>
                              </div>
                            </div>

                            <div className="relative">
                              <Progress value={percentage} className="h-3" />
                              <div className="mt-2 flex justify-between text-xs font-medium text-gray-500">
                                <span>Optimal</span>
                                <span>Needs Improvement</span>
                                <span>Poor</span>
                              </div>
                              {}
                              <div className="absolute top-0 left-[33%] h-3 w-0.5 bg-background/50" />
                              <div className="absolute top-0 left-[66%] h-3 w-0.5 bg-background/50" />
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent side="left" align="start" className="w-80 border-white/10 bg-[#0A0A0A] p-5 shadow-2xl">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 border-b border-white/10 pb-2">
                          <metric.icon className="text-primary h-4 w-4" />
                          <h4 className="font-semibold text-white">{metric.shortName} Impact</h4>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          {metric.seoImpact}
                        </p>
                        <div className="pt-2">
                          <Button variant="link" className="text-primary h-auto p-0 text-xs">
                            Read full documentation <ArrowUpRight className="ml-1 h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}